import DOMPurify from 'dompurify';
import { cn } from '../../lib/utils';
import { normalizeResponsiveSvg } from './inlineSvgFigureUtils';

type InlineSvgFigureProps = {
  svg: string;
  className?: string;
  /** Applied to the root SVG when it does not already define aria-label. */
  ariaLabel?: string;
};

function withAriaLabel(svg: string, ariaLabel?: string): string {
  if (!ariaLabel?.trim() || /aria-label\s*=/i.test(svg)) return svg;
  if (!svg.trim().startsWith('<svg')) return svg;

  const openEnd = svg.indexOf('>');
  if (openEnd < 0) return svg;

  const escaped = ariaLabel.replace(/"/g, '&quot;');
  return `${svg.slice(0, openEnd)} aria-label="${escaped}"${svg.slice(openEnd)}`;
}

export function InlineSvgFigure({ svg, className, ariaLabel }: InlineSvgFigureProps) {
  const responsiveSvg = normalizeResponsiveSvg(withAriaLabel(svg, ariaLabel));
  const safe = DOMPurify.sanitize(responsiveSvg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_ATTR: [
      'viewBox',
      'xmlns',
      'role',
      'aria-label',
      'preserveAspectRatio',
      'data-figure-key',
      'data-diagram-failed',
      'data-diagram-render-ts',
      'opacity',
      'fill',
      'stroke',
      'font-size',
      'font-family',
      'font-weight',
      'text-anchor',
      'tspan',
      'dy',
      'dx',
      'x',
      'y',
      'cx',
      'cy',
      'r',
      'rx',
      'ry',
      'width',
      'height',
      'd',
      'points',
      'transform',
    ],
  });

  if (!safe?.trim()) return null;

  return (
    <div
      className={cn(
        'my-3 w-full max-w-[38rem] overflow-x-auto text-sm [&_svg]:mx-auto [&_svg]:block [&_svg]:h-auto [&_svg]:w-full',
        className
      )}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}
